import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { PageLayout } from '../components/ui/Layout';
import { Card } from '../components/ui/Card';
import { PrimaryButton } from '../components/ui/Button';
import { TextInput } from '../components/ui/Input';
import { clsx } from 'clsx';
import { motion } from 'framer-motion';

const { FiArrowRight, FiAward } = FiIcons;

export default function Home() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    buyerName: '',
    buyerGender: 'male',
    meetingNum: '1'
  });

  const handleStart = () => {
    const sessionData = {
      userName: 'You',
      buyerName: formData.buyerName,
      buyerGender: formData.buyerGender,
      meetingIndex: formData.meetingNum
    };
    sessionStorage.setItem('ioweu_session', JSON.stringify(sessionData));
    navigate('/quiz/ioweu-standard');
  };

  const isFormValid = formData.buyerName.trim().length > 0;

  return (
    <PageLayout>
      <div className="max-w-xl mx-auto py-4 md:py-10">
        <div className="text-center mb-6 md:mb-10">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 rounded-2xl md:rounded-3xl flex items-center justify-center mx-auto mb-4 md:mb-6 text-[#0270a9]"
          >
            <SafeIcon icon={FiAward} className="text-3xl md:text-4xl" />
          </motion.div>
          <h1 className="text-2xl md:text-4xl font-black text-slate-900 mb-3 tracking-tight">The INTIMASEE Meeting Assessment</h1>
          <p className="text-slate-500 text-base md:text-lg leading-relaxed px-2">
            Your interaction with another person typically reflects the type of relationship you have with them. This simple exercise uses a meeting (or phone call) as a means of gaining an insight into that.
          </p>
        </div>

        <Card className="p-5 md:p-8 space-y-6 md:space-y-8 border-none shadow-xl md:shadow-2xl shadow-slate-200/50">
            <div className="text-center space-y-2">
                <h2 className="text-lg md:text-xl font-bold text-slate-800 uppercase tracking-widest">Instructions</h2>
                <p className="text-slate-500 text-xs md:text-sm">Nominate which meeting/interaction this was, enter the other person's name then click Start to begin.</p>
            </div>

            <div className="space-y-5 md:space-y-6 pt-2">
                
                {/* Meeting Selector */}
                <div className="space-y-2 md:space-y-3">
                    <label className="block text-lg md:text-xl font-medium text-slate-700 text-center">This was my</label>
                    <div className="grid grid-cols-3 gap-2 md:gap-3">
                        {['1', '2', '3'].map(num => (
                        <button 
                            key={num}
                            type="button"
                            onClick={() => setFormData({...formData, meetingNum: num})}
                            className={clsx(
                            "py-2 md:py-3 px-3 md:px-4 rounded-lg md:rounded-xl border-2 font-bold transition-all text-sm md:text-base",
                            formData.meetingNum === num ? "border-[#0270a9] bg-blue-50 text-[#0270a9]" : "border-slate-100 text-slate-500 hover:border-slate-200"
                            )}
                        >
                            {num === '1' ? '1st' : num === '2' ? '2nd' : '3rd+'}
                        </button>
                        ))}
                    </div>
                </div>

                {/* Name Input */}
                <div className="space-y-2 md:space-y-3">
                    <label className="block text-lg md:text-xl font-medium text-slate-700 text-center">interaction with</label>
                    <TextInput 
                        placeholder="Other person's name"
                        value={formData.buyerName}
                        onChange={e => setFormData({...formData, buyerName: e.target.value})}
                        className="text-center text-base md:text-lg py-2 md:py-3"
                        style={{ fontSize: '1.125rem' }} // Ensure font size doesn't drop too low on mobile inputs (prevents zoom)
                    />
                </div>

                {/* Gender Selector */}
                <div className="space-y-2 md:space-y-3">
                    <label className="block text-lg md:text-xl font-medium text-slate-700 text-center">who is*</label>
                    <div className="grid grid-cols-2 gap-2 md:gap-3">
                        {['male', 'female'].map(g => (
                        <button 
                            key={g}
                            type="button"
                            onClick={() => setFormData({...formData, buyerGender: g})}
                            className={clsx(
                            "py-2 md:py-3 px-3 md:px-4 rounded-lg md:rounded-xl border-2 font-bold capitalize transition-all text-sm md:text-base",
                            formData.buyerGender === g ? "border-[#0270a9] bg-blue-50 text-[#0270a9]" : "border-slate-100 text-slate-500 hover:border-slate-200"
                            )}
                        >
                            {g}
                        </button>
                        ))}
                    </div>
                    <p className="text-center text-[10px] md:text-xs text-slate-400 italic">
                        *selection personalizes feedback; it does not affect the results
                    </p>
                </div>

            </div>

          <PrimaryButton 
            disabled={!isFormValid} 
            onClick={handleStart} 
            className="w-full py-3 md:py-5 text-base md:text-lg font-black tracking-wide"
          >
            Start <SafeIcon icon={FiArrowRight} className="ml-2" />
          </PrimaryButton>
        </Card>
      </div>
    </PageLayout>
  );
}